import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock3, XCircle } from "lucide-react";
import { useOrder } from "../../features/orders/queries";
import { EmptyState, Skeleton } from "@aurelia/ui";
import { useDocumentMeta } from "../../lib/useDocumentMeta";

export function SafepayResultPage({ cancelled = false }: { cancelled?: boolean }) {
  useDocumentMeta("Safepay payment", "View the verified status of your Safepay payment.", undefined, undefined, { noindex: true });
  const [params] = useSearchParams();
  const orderId = params.get("orderId") ?? undefined;
  const orderQuery = useOrder(orderId, true);

  if (!orderId) return <main className="mx-auto max-w-xl px-6 py-24"><EmptyState tone="error" title="Missing order reference" /></main>;
  if (orderQuery.isLoading) return <main className="mx-auto max-w-xl px-6 py-24"><Skeleton className="h-52" /></main>;
  if (!orderQuery.data) return <main className="mx-auto max-w-xl px-6 py-24"><EmptyState tone="error" title="Order not found" /></main>;

  const order = orderQuery.data;
  const paid = order.paymentStatus === "PAID";
  const terminalFailure = order.paymentStatus === "FAILED" || order.paymentStatus === "CANCELLED";
  const Icon = paid ? CheckCircle2 : terminalFailure || cancelled ? XCircle : Clock3;

  return (
    <main className="mx-auto max-w-xl px-6 py-24 text-center">
      <Icon className={paid ? "mx-auto text-emerald-500" : terminalFailure || cancelled ? "mx-auto text-red-500" : "mx-auto text-amber-500"} size={48} />
      <h1 className="mt-5 text-display-sm">{paid ? "Payment confirmed" : cancelled ? "Checkout cancelled" : terminalFailure ? "Payment not completed" : "Confirming payment"}</h1>
      <p className="mt-3 text-ink-muted">
        {paid
          ? `Safepay has verified payment for ${order.orderNumber}.`
          : cancelled
            ? "No payment has been assumed from this redirect. You can retry from your order."
            : terminalFailure
              ? "Safepay did not complete this payment. You can retry from your order."
              : "We are waiting for Safepay's signed confirmation. This page updates automatically."}
      </p>
      <p className="mt-3 text-sm">Server payment status: <span className="font-medium">{order.paymentStatus}</span></p>
      <Link className="mt-7 inline-flex h-12 items-center rounded-full bg-ink px-6 text-canvas dark:bg-accent dark:text-ink" to="/account/orders">View my orders</Link>
    </main>
  );
}
